<script setup lang="ts">
//#region imports
import { computed } from 'vue';
import { useTypedStore } from '@/store';
import type UiScrollbar from '@/components/UIElements/UiScrollbar.vue';
//#endregion

//#region types
interface LanguageItem {
  languageCode: string;
  countryCode?: string;
}

interface LanguagesMeta {
  full: LanguageItem[];
  base: LanguageItem[];
  rest: LanguageItem[];
}

interface Props {
  meta?: LanguagesMeta;
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  meta: () => ({
    full: [],
    base: [],
    rest: [],
  }),
});
//#endregion

const store = useTypedStore();

const languagesFromStore = computed(() => store.state.misc?.languages || []);

function getLanguageNameByCode(languageCode: string): string  {
  return languagesFromStore.value.find((language) => language.code === languageCode)?.name || '';
}
</script>

<template>
  <div class="activity-actions-languages">
    <div
      v-for="language in props.meta.base"
      :key="language.languageCode"
      class="activity-actions-languages__item"
    >
      <Flag
        v-if="language.countryCode"
        :iso="language.countryCode"
      />
      <UiIcon
        v-else
        name="globe"
      />
      <div class="activity-actions-languages__item-name">
        {{ language.languageCode }}
      </div>
    </div>
    <ElTooltip
      v-if="props.meta.rest.length > 0"
      :show-after="300"
      popper-class="activity-actions-languages__popper"
      placement="top"
      effect="light"
    >
      <div class="activity-actions-languages__item">
        <div class="activity-actions-languages__item-name">
          + {{ props.meta!.rest.length }}
        </div>
      </div>
      <template v-slot:content>
        <div>
          <CardTitle
            :title="$t('crm.activities.actions.translation')"
          />
          <UiScrollbar
            wrap-style="max-height: 320px;"
          >
            <div class="t:px-8">
              <CardItem
                v-for="(languageRest, id) in props.meta.rest"
                :key="languageRest.languageCode"
                no-value
                :no-border="id === 0"
              >
                <template v-slot:title>
                  <div class="t:flex t:items-center t:gap-4">
                    <Flag
                      v-if="languageRest.countryCode"
                      :iso="languageRest.countryCode"
                    />
                    <UiIcon
                      v-else
                      name="globe"
                    />
                    <span class="activity-actions-languages__item-name_rest">
                      {{ getLanguageNameByCode(languageRest.languageCode) }}
                    </span>
                  </div>
                </template>
              </CardItem>
            </div>
          </UiScrollbar>
        </div>
      </template>
    </ElTooltip>
  </div>
</template>

<style lang="scss">
.activity-actions-languages {
  display: flex;
  align-items: center;
  &__item {
    display: flex;
    height: 24px;
    padding: 4px 8px 4px 6px;
    justify-content: center;
    align-items: center;
    gap: 4px;
    border-radius: 100px;
    border: 1px solid var(--border-default);
    background: var(--background-surface);
    margin-left: -6px;

    &:first-child {
      margin-left: 0;
    }
    &:last-child {
      padding: 4px 6px;
    }
  }
  &__item-name{
    color: var(--text-body-secondary);
    font-size: 10px;
    font-weight: 500;
    line-height: 100%; /* 10px */
    text-transform: uppercase;
    &_rest {
      color: var(--text-body);
      font-size: 14px;
      font-weight: 400;
      line-height: 18px;
      text-transform: capitalize;
    }
  }
  &__popper.el-popper.el-tooltip {
    box-shadow: 0 4px 32px 0 rgba(0, 0, 0, 0.16);
    border: none;
    border-radius: 8px;
    background: var(--background-body);
    padding: 16px;
    width: 320px;
    max-height: 400px;
  }
}
</style>
